'use client';

import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { applySampledTick, expireTrade, initialState, nextDelayMs, type PreviewSimState, samplePreviewTick } from '@/lib/launchpad/preview-simulation';

// Bubble lifetime on the live-trades overlay. With the faster tick cadence
// (180–700 ms), 1500 ms keeps roughly 3–8 bubbles in flight at once — busy
// without becoming an unreadable swarm.
const TRADE_HOLD_MS = 1500;
export interface PreviewSimulationOptions {
  /** Multiplier on tick delay and trade-hold window (1 = default). */
  cadenceScale?: number;
  /** Per-tick cadence override; takes precedence over `cadenceScale` when set. */
  cadenceScaleForTick?: (completedTicks: number) => number;
  /** When false, timers stop and the last state is held. Default true. */
  enabled?: boolean;
  /** Stop scheduling ticks after this many applyTick calls per enabled session. */
  maxTicks?: number;
  /** Bump to restart the burst from initial state while enabled. */
  replayKey?: number;
  /** Fires once when a maxTicks burst finishes (or timers stop while enabled). */
  onBurstComplete?: () => void;
}

/**
 * Drives the proposal-creation preview card so it visually behaves like a
 * live market. Each tick injects a fake trade (volume + prize-pool grow,
 * shares drift while staying summed to 1) and pushes a `LiveTrade` that
 * `<MarketRowCard>`'s built-in `<LiveTradesOverlay>` renders as a floating
 * bubble. Trades are removed after `TRADE_HOLD_MS` so the bubble overlay
 * doesn't accumulate.
 *
 * Re-initialises whenever `outcomeCount` changes (user adds/removes an
 * outcome on the form). Honors `prefers-reduced-motion` by freezing on the
 * deterministic initial state.
 */
export function usePreviewSimulation(outcomeCount, startingShares, options) {
  const $ = _c(14);
  const cadenceScale = options?.cadenceScale ?? 1;
  const cadenceScaleForTick = options?.cadenceScaleForTick;
  const enabled = options?.enabled ?? true;
  const maxTicks = options?.maxTicks;
  const replayKey = options?.replayKey ?? 0;
  const onBurstComplete = options?.onBurstComplete;
  const reduced = useReducedMotion();
  let t0;
  if ($[0] !== outcomeCount || $[1] !== startingShares) {
    t0 = () => initialState(outcomeCount, startingShares);
    $[0] = outcomeCount;
    $[1] = startingShares;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [state, setState] = useState(t0);
  const tickCountRef = useRef(0);
  const burstCompleteRef = useRef(false);
  const prevEnabledRef = useRef(null);
  const prevOutcomeCountRef = useRef(outcomeCount);
  const prevReplayKeyRef = useRef(replayKey);
  const [trackedOutcomeCount, setTrackedOutcomeCount] = useState(outcomeCount);
  if (outcomeCount !== trackedOutcomeCount) {
    setTrackedOutcomeCount(outcomeCount);
    setState(initialState(outcomeCount, startingShares));
  }
  let t1;
  let t2;
  if ($[3] !== cadenceScale || $[4] !== cadenceScaleForTick || $[5] !== enabled || $[6] !== maxTicks || $[7] !== onBurstComplete || $[8] !== outcomeCount || $[9] !== reduced || $[10] !== replayKey || $[11] !== startingShares) {
    t1 = () => {
      const wasEnabled = prevEnabledRef.current;
      prevEnabledRef.current = enabled;
      const timers = createTimeoutGroup();
      if (outcomeCount !== prevOutcomeCountRef.current) {
        prevOutcomeCountRef.current = outcomeCount;
        tickCountRef.current = 0;
        burstCompleteRef.current = false;
      }
      if (reduced === true) {
        return timers.cancelAll;
      }
      if (outcomeCount < 2) {
        return timers.cancelAll;
      }
      const shouldReplay = replayKey !== prevReplayKeyRef.current;
      if (shouldReplay) {
        prevReplayKeyRef.current = replayKey;
        tickCountRef.current = 0;
        burstCompleteRef.current = false;
        setState(initialState(outcomeCount, startingShares));
      }
      if (!enabled) {
        return timers.cancelAll;
      }
      const shouldRestartBurst = !shouldReplay && wasEnabled === false && burstCompleteRef.current === true;
      if (shouldRestartBurst) {
        tickCountRef.current = 0;
        burstCompleteRef.current = false;
        setState(initialState(outcomeCount, startingShares));
      }
      const resolveCadenceScale = completedTicks => cadenceScaleForTick?.(completedTicks) ?? cadenceScale;
      const markBurstComplete = () => {
        if (burstCompleteRef.current) {
          return;
        }
        burstCompleteRef.current = true;
        onBurstComplete?.();
      };
      const scheduleTick = () => {
        if (maxTicks !== undefined && tickCountRef.current >= maxTicks) {
          markBurstComplete();
          return;
        }
        const tickCadence = resolveCadenceScale(tickCountRef.current);
        const delay = nextDelayMs(Math.random, tickCadence);
        timers.schedule(() => {
          if (maxTicks !== undefined && tickCountRef.current >= maxTicks) {
            markBurstComplete();
            return;
          }
          const appliedCadence = resolveCadenceScale(tickCountRef.current);
          tickCountRef.current = tickCountRef.current + 1;
          const sample = samplePreviewTick(Math.random, _temp);
          setState(prev => applySampledTick(prev, sample).next);
          timers.schedule(() => {
            setState(current => expireTrade(current, sample.tradeId));
          }, TRADE_HOLD_MS * appliedCadence);
          if (maxTicks !== undefined && tickCountRef.current >= maxTicks) {
            markBurstComplete();
            return;
          }
          scheduleTick();
        }, delay);
      };
      scheduleTick();
      return timers.cancelAll;
    };
    t2 = [cadenceScale, cadenceScaleForTick, enabled, maxTicks, onBurstComplete, outcomeCount, reduced, replayKey, startingShares];
    $[3] = cadenceScale;
    $[4] = cadenceScaleForTick;
    $[5] = enabled;
    $[6] = maxTicks;
    $[7] = onBurstComplete;
    $[8] = outcomeCount;
    $[9] = reduced;
    $[10] = replayKey;
    $[11] = startingShares;
    $[12] = t1;
    $[13] = t2;
  } else {
    t1 = $[12];
    t2 = $[13];
  }
  useEffect(t1, t2);
  return state;
}
function _temp() {
  return crypto.randomUUID();
}
interface TimeoutGroup {
  readonly schedule: (callback: () => void, delayMs: number) => void;
  readonly cancelAll: () => void;
}
function createTimeoutGroup(): TimeoutGroup {
  const pending = new Set<ReturnType<typeof setTimeout>>();
  let cancelled = false;
  const schedule = (callback: () => void, delayMs: number): void => {
    if (cancelled) return;
    const timer = setTimeout(() => {
      pending.delete(timer);
      if (!cancelled) callback();
    }, delayMs);
    pending.add(timer);
  };
  const cancelAll = (): void => {
    cancelled = true;
    for (const timer of pending) clearTimeout(timer);
    pending.clear();
  };
  return {
    schedule,
    cancelAll
  };
}